import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PageWrapper from "../pageWrapper";
import useLazyLoading from "../../hooks/useLazyLoading";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";

//CSS
import "./tagPage.css";
import "../0PageStyles.css";

//Components
import Title from "../../Components/Other Components/Title";
import ColorCircle from "../../Components/Tag Components/colorCircle";
import SearchBar from "../../Components/Other Components/searchBar";
import TagCreationPopup from "./tagCreationPopup";
import TagEditPopup from "./tagEditPopup";
import SortComponent from "../../Components/Other Components/sortComponent";
import ItemCard from "../../Components/Item Components/itemCard";
import ItemCardHeader from "../../Components/Item Components/itemCardHeader";
import PageLoader from "../../Components/Other Components/pageLoader";
import Button from "../../Components/Other Components/button";

//Functions and hooks
import { GetTags } from "../../BackendAccess/TagHooks";
import { useErrorHandler } from "../../hooks/errorHandler";
import { useNotification } from "../../Contexts/NotificationContext";
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import { useTranslation } from "react-i18next";
import { usePopup } from "../../Contexts/PopupContext";

export default function TagPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFilteredItems, setSearchFilteredItems] = useState([]);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const { showNotification } = useNotification();

  const lazyLoadItemLoadCount = 20;
  const lazyLoadPixelThreshold = 200;

  useErrorHandler(error, showNotification, setPageError);

  const isNarrow = useNarrowMode();

  const { t } = useTranslation();

  const { showPopup, removePopup } = usePopup();

  const showCreationPopup = () => {
    const key = Date.now();
    showPopup(
      <TagCreationPopup
        onCancel={() => {
          handleCreateCancel();
          removePopup(key);
        }}
        onCreate={() => {
          onCreatedTag();
          removePopup(key);
        }}
      />,
      key
    );
  };
  const showEditPopup = (currentID) => {
    const key = Date.now();
    showPopup(
      <TagEditPopup
        onCancel={() => {
          handleEditCancel();
          removePopup(key);
        }}
        onEdit={() => {
          onEditedTag();
          removePopup(key);
        }}
        currentID={currentID}
        onDelete={() => {
          onDeletedTag();
          removePopup(key);
        }}
      />,
      key
    );
  };

  // Fetch items from the server
  const fetchTags = async () => {
    try {
      setLoading(true);
      const [data, error] = await fetchWithTimeout(GetTags);
      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        setTags(data);
      }
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, [location.pathname]);

  const handleEditClick = async (id) => {
    showEditPopup(id);
  };
  const handleEditCancel = async () => {};
  const onEditedTag = async () => {
    await fetchTags();
  };
  const onDeletedTag = async () => {
    await fetchTags();
  };

  const handleCreateClick = async () => {
    showCreationPopup();
  };
  const handleCreateCancel = () => {};
  const onCreatedTag = async () => {
    await fetchTags();
  };

  //********************Searching and sorting************************************** */
  const handleSearchChange = (term) => {
    setSearchTerm(term);
  };

  useEffect(() => {
    const newSearchFilteredItems = tags.filter((item) => {
      const lowerCaseTerm = searchTerm.toLowerCase();
      return (
        item.Name.toLowerCase().includes(lowerCaseTerm) ||
        item.TagID.toString().toLowerCase().includes(lowerCaseTerm)
      );
    });
    setSearchFilteredItems(newSearchFilteredItems);
  }, [searchTerm, tags]);

  const nameSortFunction = (direction) => {
    var tagsSorted = [...tags].sort((a, b) =>
      a.Name.toUpperCase() > b.Name.toUpperCase()
        ? direction * 1
        : -1 * direction
    );
    setTags(tagsSorted);
  };
  const idSortFunction = (direction) => {
    var tagsSorted = [...tags].sort((a, b) =>
      a.TagID > b.TagID ? direction * 1 : -1 * direction
    );
    setTags(tagsSorted);
  };
  //********************Searching and sorting end ************************************** */
  ////////////////////////////////////////////////Lazy loading///////////////////////////////////////
  const { loadedItems, itemContainerRef } = useLazyLoading(
    searchFilteredItems,
    lazyLoadItemLoadCount,
    lazyLoadPixelThreshold
  );
  ////////////////////////////////////////////////Lazy loading end///////////////////////////////////////

  ////////////////////////////////////////Creating cards///////////////////////////////////////////
  const propertiesToShow = {
    ID: { Show: true, Width: 1 },
    Name: { Show: true, Width: 3 },
    Color: { Show: true, Width: 2 },
  };

  const columnWidths = Object.values(propertiesToShow).map(
    (item) => item.Width
  );
  const createHeaderCardProps = () => {
    const cardPropArray = [
      { Value: "ID", columnWidth: columnWidths[0] },
      { Value: "Name", columnWidth: columnWidths[1] },
      { Value: "Color", columnWidth: columnWidths[2] },
    ];

    const newCardPropArr = cardPropArray.filter((item) => {
      return propertiesToShow[item.Value].Show;
    });
    return newCardPropArr;
  };
  const createCardProps = (item) => {
    const cardPropArray = [
      { Name: "ID", Value: item.TagID, columnWidth: columnWidths[0] },
      { Name: "Name", Value: item.Name, columnWidth: columnWidths[1] },
      {
        Name: "Color",
        Value: (
          <ColorCircle
            clickable={false}
            color={item.Color}
            onClick={() => {}}
          />
        ),
        columnWidth: columnWidths[2],
      },
    ];

    const newCardPropArr = cardPropArray.filter((item) => {
      return propertiesToShow[item.Name].Show;
    });
    return newCardPropArr;
  };

  ////////////////////////////////////////Creating cards end///////////////////////////////////////////

  const tutorialSteps = [
    {
      target: null,
      title: t("Your tags", { ns: "tutorial" }),
      message: t(
        "This is your Tags page - tags help you group and filter items by category, storage requirements, fragility, or anything else you need. Every tag has a name and a color for quick visual recognition.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".search-bar-container",
      title: t("Search", { ns: "tutorial" }),
      message: t("Search for a tag by name or ID.", { ns: "tutorial" }),
    },
    {
      target: ".CardHeader .btn-positive",
      title: t("Create a tag", { ns: "tutorial" }),
      message: t(
        "Opens a small popup where you name the tag and pick its color.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".item-card-container .Card:first-child",
      title: t("Tag rows", { ns: "tutorial" }),
      message: t(
        "Each row is one tag. Click Edit on a row to rename it, change its color, or delete it.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="tags" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <Title className="mt-3">Tags</Title>
          <div className="search-bar-container">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              placeholder={t("Search tags...", { ns: "tags" })}
            />
          </div>
          <div className="sortAndFilterBar">
            <SortComponent
              sortOpts={["Name", "ID"]}
              sortOptFunctions={[nameSortFunction, idSortFunction]}
            />
          </div>

          <div className="item-card-and-header-container">
            <ItemCardHeader
              propertiesToShow={createHeaderCardProps()}
              bBtnVisible={true}
              bBtnSectionVisible={true}
              btnSectionWidth={1}
              onClick={handleCreateClick}
              btnText={"Create"}
            />
            <div className="item-card-container" ref={itemContainerRef}>
              {loadedItems.map((item) => (
                <ItemCard
                  key={item.TagID}
                  ItemID={item.TagID}
                  propertiesToShow={createCardProps(item)}
                  bBtnVisible={true}
                  bBtnSectionVisible={true}
                  btnSectionWidth={1}
                  onClick={() => {
                    handleEditClick(item.TagID);
                  }}
                  btnText={"Edit"}
                />
              ))}
            </div>
          </div>
          {isNarrow ? (
            <div className="item-view-narrow-btn-container">
              <Button
                btnStyle={"btn-positive"}
                icon="create"
                onClick={handleCreateClick}
              >
                {t("Create")}
              </Button>
            </div>
          ) : null}
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
